'use client'

import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline'
import SuggestionDropdown, {
    handleSuggestionKeyDown,
    type SuggestionItem
} from '@tebuto/forms/address-autocomplete/SuggestionDropdown'
import { useCityLookupByZip } from '@tebuto/forms/address-autocomplete/use-address-lookup'
import { formatZipInput } from '@tebuto/functions/validate'
import { cn } from '@tebuto/lib/utils'
import { useField, useFormikContext } from 'formik'
import { type ChangeEvent, type JSX, useEffect, useMemo, useState } from 'react'
import { Tooltip } from 'react-tooltip'
import Description from './Description'
import ErrorMessage from './ErrorMessage'
import FormField, { type IFormField } from './Field'
import { Label } from './Label'
import { useFieldErrorVisible } from './useFieldErrorVisible'

type ZipFormFieldProps = Omit<IFormField, 'formatter' | 'type'> & {
    /** Formik field name of the sibling city field — enables city autofill on complete zip input */
    cityFieldName?: string
    /** Public API base URL override for the address lookup (used by the embedded widget) */
    lookupBaseUrl?: string
}

export default function ZipFormField({
    className = '',
    fieldClassName,
    labelClassName,
    label,
    name,
    autoComplete = 'postal-code',
    labelTooltip,
    inputTooltip,
    description,
    placeholder,
    mandatory,
    readOnly,
    showErrors = true,
    height = 'default',
    cityFieldName,
    lookupBaseUrl,
    'data-testid': testId
}: ZipFormFieldProps): JSX.Element {
    const showFieldError = useFieldErrorVisible(name)
    const [field, , helpers] = useField<string>(name)
    const { setFieldValue } = useFormikContext<unknown>()
    const isWidgetField = fieldClassName?.includes('widget-text-primary') ?? false
    const inputTooltipId = inputTooltip ? `${name}-input` : undefined

    // Only zips the user actually typed trigger a lookup — never initial form values
    const [typedZip, setTypedZip] = useState<string>()
    const [dropdownOpen, setDropdownOpen] = useState(false)
    const [activeIndex, setActiveIndex] = useState(0)
    const { localities } = useCityLookupByZip(typedZip, { enabled: !!cityFieldName, baseUrl: lookupBaseUrl })

    const items: SuggestionItem[] = useMemo(
        () => localities.map((locality) => ({ id: `${locality.postalCode}-${locality.name}`, label: locality.name })),
        [localities]
    )

    useEffect(() => {
        if (!cityFieldName || !typedZip) {
            return
        }
        if (localities.length === 1) {
            // Single match — autofill the city directly
            void setFieldValue(cityFieldName, localities[0].name)
            setDropdownOpen(false)
        } else if (localities.length > 1) {
            // Multiple localities share this zip — let the user pick
            setActiveIndex(0)
            setDropdownOpen(true)
        } else {
            setDropdownOpen(false)
        }
    }, [localities, cityFieldName, typedZip, setFieldValue])

    if (readOnly) {
        return (
            <FormField
                className={className}
                fieldClassName={fieldClassName}
                labelClassName={labelClassName}
                label={label}
                name={name}
                autoComplete={autoComplete}
                labelTooltip={labelTooltip}
                inputTooltip={inputTooltip}
                description={description}
                placeholder={placeholder}
                mandatory={mandatory}
                readOnly={readOnly}
                showErrors={showErrors}
                height={height}
                data-testid={testId}
            />
        )
    }

    function getHeight(): string {
        switch (height) {
            case 'full':
                return 'h-full'
            case 'small':
                return 'h-8'
            default:
                return 'h-10'
        }
    }

    function inputClassName(): string {
        let classes =
            'h-full rounded-lg flex-1 min-w-0 block w-full px-2 py-1 border text-sm border-secondary text-foreground-tertiary shadow-xs focus:outline-none focus:ring-0 focus:border-primary-500'
        if (!isWidgetField) {
            classes += ' placeholder:text-foreground-muted'
        }
        if (showFieldError) {
            classes += ' border-red-500'
        }
        return classes
    }

    function handleChange(event: ChangeEvent<HTMLInputElement>): void {
        const formattedZip = formatZipInput(event.target.value)
        void helpers.setValue(formattedZip)
        setTypedZip(formattedZip)
        if (formattedZip.length < 5) {
            setDropdownOpen(false)
        }
    }

    function selectLocality(item: SuggestionItem): void {
        if (cityFieldName) {
            void setFieldValue(cityFieldName, item.label)
        }
        setDropdownOpen(false)
    }

    return (
        <div className={cn('relative', className)}>
            {label ? (
                <div className="flex w-full items-center justify-between gap-2">
                    <Label htmlFor={name} mandatory={mandatory} className={labelClassName}>
                        {label}
                    </Label>
                    {labelTooltip ? (
                        <QuestionMarkCircleIcon
                            className="h-5 w-5 shrink-0 text-foreground-muted inline"
                            data-tooltip-id={name}
                            data-tooltip-content={labelTooltip}
                        />
                    ) : null}
                </div>
            ) : null}
            <div
                className={cn('flex rounded-md shadow-xs relative', getHeight())}
                {...(inputTooltipId
                    ? {
                          'data-tooltip-id': inputTooltipId,
                          'data-tooltip-content': inputTooltip
                      }
                    : {})}
            >
                <input
                    data-testid={testId ?? `field-${name}`}
                    id={name}
                    name={name}
                    type="text"
                    inputMode="numeric"
                    autoComplete={autoComplete}
                    maxLength={5}
                    className={cn(inputClassName(), fieldClassName)}
                    required={mandatory}
                    placeholder={placeholder}
                    value={field.value ?? ''}
                    onChange={handleChange}
                    onKeyDown={(event) =>
                        handleSuggestionKeyDown(event, {
                            open: dropdownOpen,
                            items,
                            activeIndex,
                            setActiveIndex,
                            onSelect: selectLocality,
                            onClose: () => setDropdownOpen(false)
                        })
                    }
                    onBlur={(event) => {
                        setDropdownOpen(false)
                        field.onBlur(event)
                    }}
                    role="combobox"
                    aria-expanded={dropdownOpen}
                    aria-controls={`${name}-suggestions`}
                />
                {dropdownOpen ? (
                    <SuggestionDropdown
                        id={`${name}-suggestions`}
                        items={items}
                        activeIndex={activeIndex}
                        onSelect={selectLocality}
                        onActiveIndexChange={setActiveIndex}
                        isWidget={isWidgetField}
                    />
                ) : null}
            </div>
            {labelTooltip ? <Tooltip id={name} /> : null}
            {inputTooltipId ? (
                <Tooltip
                    id={inputTooltipId}
                    positionStrategy="fixed"
                    opacity={1}
                    className="z-9999! max-w-xs! rounded! bg-primary-800! p-3! text-xs! font-medium! leading-snug! text-white!"
                />
            ) : null}
            {showErrors ? <ErrorMessage name={name} /> : null}
            <Description description={description} />
        </div>
    )
}
